// =============================================================================
// NotesToHankContext.tsx — React context for Notes-to-Hank text chat (web port)
// Uses localStorage instead of AsyncStorage for persistence.
// =============================================================================

import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LOG } from '../../common/Utilities';
import featureFlag from '../../common/featureFlag';
import { getIdToken } from '../../common/utils/authUtils';

import type {
  ChatMessage,
  NoteMessage,
  NoteConnectionState,
  AttachmentItem,
  ConversationContextEntry,
  PhotoMetadata,
  PersistedConversation,
} from '../../services/notesToHank/NotesToHankTypes';
import { NotesToHankAdapter } from '../../services/notesToHank/NotesToHankAdapter';
import {
  buildInitialMessage,
  buildFollowUpMessage,
} from '../../services/notesToHank/notesToHankMessageUtils';
import { generateMockResponse } from '../../services/notesToHank/mockNotesToHankResponse';

const LOG_PREFIX = '[NTH-Context]';

// -----------------------------------------------------------------------------
// localStorage Key Helper
// -----------------------------------------------------------------------------

const getStorageKey = (dealId: string): string => `notesToHank:${dealId}`;

// -----------------------------------------------------------------------------
// Context Type
// -----------------------------------------------------------------------------

export interface NotesToHankContextType {
  connectionState: NoteConnectionState;
  messages: ChatMessage[];
  isWaitingForResponse: boolean;
  inputText: string;
  setInputText: (text: string) => void;
  pendingPhotos: AttachmentItem[];
  pendingAttachments: AttachmentItem[];
  selectedAnswerIds: string[];
  pressedActionButtonIds: string[];
  sendMessage: () => void;
  selectAnswer: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText?: string
  ) => void;
  pressActionButton: (
    actionId: string,
    label?: string,
    clientCommand?: string
  ) => void;
  addAttachment: (attachment: AttachmentItem) => void;
  addAttachments: (attachments: AttachmentItem[]) => void;
  removeAttachment: (index: number) => void;
  retryLastMessage: () => void;
  clearConversation: () => void;
}

// -----------------------------------------------------------------------------
// Create Context
// -----------------------------------------------------------------------------

const NotesToHankContext = createContext<NotesToHankContextType | undefined>(
  undefined
);

// -----------------------------------------------------------------------------
// Provider Props
// -----------------------------------------------------------------------------

export interface NotesToHankProviderProps {
  children: ReactNode;
  dealId: string | null | undefined;
  websocketUrl?: string;
  onStartHank?: () => void;
  onStartManual?: () => void;
  onOpenAttachmentPicker?: () => void;
  onOrderEstimate?: () => void;
  onSubmitClaim?: () => void;
}

// -----------------------------------------------------------------------------
// WebSocket URLs
// -----------------------------------------------------------------------------

const NOTES_DEV_WEBSOCKET_URL =
  'ws://paiv-agents-nlb-d254e1a26657601f.elb.us-west-2.amazonaws.com:8080/ws';
const NOTES_PROD_WEBSOCKET_URL =
  'ws://paiv-agents-nlb-d254e1a26657601f.elb.us-west-2.amazonaws.com:8081/ws';

const DEFAULT_NOTES_WEBSOCKET_URL = NOTES_DEV_WEBSOCKET_URL;

export { NOTES_DEV_WEBSOCKET_URL, NOTES_PROD_WEBSOCKET_URL };

// -----------------------------------------------------------------------------
// Provider Component
// -----------------------------------------------------------------------------

export const NotesToHankProvider: React.FC<NotesToHankProviderProps> = ({
  children,
  dealId,
  websocketUrl = DEFAULT_NOTES_WEBSOCKET_URL,
  onStartHank,
  onStartManual,
  onOpenAttachmentPicker,
  onOrderEstimate,
  onSubmitClaim,
}) => {
  // =========================================================================
  // State
  // =========================================================================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [connectionState, setConnectionState] =
    useState<NoteConnectionState>('disconnected');
  const [inputText, setInputText] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState<AttachmentItem[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<
    AttachmentItem[]
  >([]);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<string[]>([]);
  const [pressedActionButtonIds, setPressedActionButtonIds] = useState<
    string[]
  >([]);

  // =========================================================================
  // Refs
  // =========================================================================

  const adapterRef = useRef<NotesToHankAdapter | null>(null);
  const hasSentInitialRef = useRef(false);
  const shouldIncludeContextRef = useRef(false);
  const lastUserMessageRef = useRef<NoteMessage | null>(null);
  const dealIdRef = useRef<string | null | undefined>(dealId);

  useEffect(() => {
    dealIdRef.current = dealId;
  }, [dealId]);

  // =========================================================================
  // Build conversation context from messages
  // =========================================================================

  const buildConversationContext =
    useCallback((): ConversationContextEntry[] => {
      return messages.map((msg) => ({
        role: msg.sender === 'user' ? ('client' as const) : ('server' as const),
        message: {
          type: 'note_message' as const,
          event_type:
            msg.sender === 'user'
              ? ('follow_up' as const)
              : ('response' as const),
          deal_id: dealIdRef.current ?? '',
          text: msg.text,
          photos: msg.photos,
          questions: msg.questions,
          action_buttons: msg.actionButtons,
          timestamp: msg.timestamp,
        },
        timestamp: msg.timestamp,
      }));
    }, [messages]);

  // =========================================================================
  // localStorage Persistence
  // =========================================================================

  const persistConversation = useCallback((currentMessages: ChatMessage[]) => {
    const currentDealId = dealIdRef.current;
    if (!currentDealId) {
      return;
    }

    try {
      const context = currentMessages.map((msg) => ({
        role: msg.sender === 'user' ? ('client' as const) : ('server' as const),
        message: {
          type: 'note_message' as const,
          event_type:
            msg.sender === 'user'
              ? ('follow_up' as const)
              : ('response' as const),
          deal_id: currentDealId,
          text: msg.text,
          photos: msg.photos,
          questions: msg.questions,
          action_buttons: msg.actionButtons,
          timestamp: msg.timestamp,
        },
        timestamp: msg.timestamp,
      }));

      const data: PersistedConversation = {
        messages: currentMessages,
        context,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(getStorageKey(currentDealId), JSON.stringify(data));
    } catch (error) {
      LOG.error(
        `${LOG_PREFIX} Failed to persist conversation to localStorage`,
        error
      );
    }
  }, []);

  const loadConversation = useCallback(
    (loadDealId: string): PersistedConversation | null => {
      try {
        const raw = localStorage.getItem(getStorageKey(loadDealId));
        if (raw === null) {
          return null;
        }
        return JSON.parse(raw) as PersistedConversation;
      } catch (error) {
        LOG.error(
          `${LOG_PREFIX} Failed to load conversation from localStorage`,
          error
        );
        return null;
      }
    },
    []
  );

  // =========================================================================
  // Convert pending attachments to PhotoMetadata
  // =========================================================================

  const getPhotoPayload = useCallback((): PhotoMetadata[] => {
    const allPending = [...pendingPhotos, ...pendingAttachments];
    return allPending.map((item) => ({
      uri: item.uri,
      timestamp: item.timestamp,
    }));
  }, [pendingPhotos, pendingAttachments]);

  // =========================================================================
  // Adapter Callbacks
  // =========================================================================

  const handleAdapterMessage = useCallback(
    (msg: NoteMessage) => {
      if (msg.event_type === 'response') {
        const aiMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'ai',
          text: msg.text ?? '',
          timestamp: msg.timestamp ?? Date.now(),
          questions: msg.questions,
          actionButtons: msg.action_buttons,
        };
        setMessages((prev) => {
          const updated = [...prev, aiMessage];
          persistConversation(updated);
          return updated;
        });
        setIsWaitingForResponse(false);
      } else if (msg.event_type === 'error') {
        const errorMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'ai',
          text: msg.error ?? 'An error occurred. Please try again.',
          timestamp: msg.timestamp ?? Date.now(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsWaitingForResponse(false);
      }
    },
    [persistConversation]
  );

  const handleAdapterError = useCallback((error: Error) => {
    LOG.error(`${LOG_PREFIX} Adapter error:`, error.message);

    const errorMessage: ChatMessage = {
      id: uuidv4(),
      sender: 'ai',
      text: error.message || 'Connection error. Please try again.',
      timestamp: Date.now(),
      isError: true,
    };
    setMessages((prev) => [...prev, errorMessage]);
    setIsWaitingForResponse(false);
  }, []);

  const handleConnectionChange = useCallback((state: NoteConnectionState) => {
    LOG.info(`${LOG_PREFIX} Connection state changed: ${state}`);
    setConnectionState(state);

    if (state === 'connected') {
      shouldIncludeContextRef.current = true;

      if (
        !hasSentInitialRef.current &&
        dealIdRef.current &&
        adapterRef.current
      ) {
        LOG.info(
          `${LOG_PREFIX} Connected - sending initial message to get welcome response`
        );
        const initialMsg: NoteMessage = {
          type: 'note_message',
          event_type: 'initial',
          deal_id: dealIdRef.current,
          text: '',
          photos: [],
          timestamp: Date.now(),
        };
        hasSentInitialRef.current = true;
        setIsWaitingForResponse(true);
        try {
          adapterRef.current.sendMessage(initialMsg);
        } catch (err) {
          LOG.error(
            `${LOG_PREFIX} Failed to send initial message:`,
            String(err)
          );
          setIsWaitingForResponse(false);
        }
      }
    }
  }, []);

  // =========================================================================
  // Adapter Lifecycle
  // =========================================================================

  useEffect(() => {
    if (!dealId) {
      LOG.info(`${LOG_PREFIX} No deal ID, skipping adapter creation`);
      return;
    }

    // Mock mode
    if (featureFlag('mockNotesToHank')) {
      LOG.info(`${LOG_PREFIX} Mock mode enabled - skipping real adapter`);
      setConnectionState('connected');
      hasSentInitialRef.current = false;
      shouldIncludeContextRef.current = false;

      const autoInitial: NoteMessage = {
        type: 'note_message',
        event_type: 'initial',
        deal_id: dealId,
        text: '',
        photos: [],
        timestamp: Date.now(),
      };
      hasSentInitialRef.current = true;
      setIsWaitingForResponse(true);
      const delay = 300 + Math.random() * 500;
      const timer = setTimeout(() => {
        const mockResponse = generateMockResponse(autoInitial);
        handleAdapterMessage(mockResponse);
      }, delay);

      return () => {
        clearTimeout(timer);
      };
    }

    LOG.info(`${LOG_PREFIX} Creating adapter for deal: ${dealId}`);

    const adapter = new NotesToHankAdapter({
      url: websocketUrl,
      getAuthToken: async () => {
        const token = await getIdToken();
        return token ?? '';
      },
    });

    adapter.onMessage = handleAdapterMessage;
    adapter.onError = handleAdapterError;
    adapter.onConnectionChange = handleConnectionChange;

    adapterRef.current = adapter;

    adapter.connect().catch((err) => {
      LOG.error(`${LOG_PREFIX} Failed to connect adapter:`, String(err));
    });

    hasSentInitialRef.current = false;
    shouldIncludeContextRef.current = false;

    return () => {
      LOG.info(`${LOG_PREFIX} Cleaning up adapter for deal: ${dealId}`);
      adapter.onMessage = null;
      adapter.onError = null;
      adapter.onConnectionChange = null;
      adapter.disconnect();
      adapter.cleanup();
      adapterRef.current = null;
    };
  }, [
    dealId,
    websocketUrl,
    handleAdapterMessage,
    handleAdapterError,
    handleConnectionChange,
  ]);

  // =========================================================================
  // Load Persisted Conversation on Mount / Deal ID Change
  // =========================================================================

  useEffect(() => {
    if (!dealId) {
      return;
    }

    const persisted = loadConversation(dealId);
    if (persisted && persisted.messages && persisted.messages.length > 0) {
      LOG.info(
        `${LOG_PREFIX} Loaded ${persisted.messages.length} persisted messages for deal: ${dealId}`
      );
      setMessages(persisted.messages);
      shouldIncludeContextRef.current = true;
    } else {
      setMessages([]);
    }
  }, [dealId, loadConversation]);

  // =========================================================================
  // Actions
  // =========================================================================

  const doSend = useCallback(
    (overrideText?: string, overrideActionIds?: string[]) => {
      const isMock = featureFlag('mockNotesToHank');
      const adapter = adapterRef.current;
      if (!isMock && !adapter) {
        LOG.warn(`${LOG_PREFIX} Cannot send: no adapter`);
        return;
      }
      if (!dealIdRef.current) {
        LOG.warn(`${LOG_PREFIX} Cannot send: no deal ID`);
        return;
      }

      const currentText = (overrideText ?? inputText).trim();
      const currentPhotos = getPhotoPayload();
      const currentAnswerIds = [...selectedAnswerIds];
      const currentActionIds = overrideActionIds ?? [...pressedActionButtonIds];

      if (
        currentText.length === 0 &&
        currentAnswerIds.length === 0 &&
        currentActionIds.length === 0
      ) {
        return;
      }

      let msg: NoteMessage;

      if (!hasSentInitialRef.current || shouldIncludeContextRef.current) {
        const context = shouldIncludeContextRef.current
          ? buildConversationContext()
          : undefined;
        msg = buildInitialMessage(
          dealIdRef.current,
          currentText,
          currentPhotos,
          context
        );
        hasSentInitialRef.current = true;
        shouldIncludeContextRef.current = false;
      } else {
        msg = buildFollowUpMessage(
          dealIdRef.current,
          currentText,
          currentPhotos,
          currentAnswerIds,
          currentActionIds
        );
      }

      lastUserMessageRef.current = msg;

      const userMessage: ChatMessage = {
        id: uuidv4(),
        sender: 'user',
        text: currentText,
        timestamp: Date.now(),
        photos: currentPhotos.length > 0 ? currentPhotos : undefined,
        selectedAnswers:
          currentAnswerIds.length > 0
            ? currentAnswerIds.reduce(
                (acc, id) => ({ ...acc, [id]: id }),
                {} as Record<string, string>
              )
            : undefined,
        pressedActionButtonIds:
          currentActionIds.length > 0 ? currentActionIds : undefined,
      };
      setMessages((prev) => {
        const updated = [...prev, userMessage];
        persistConversation(updated);
        return updated;
      });

      setIsWaitingForResponse(true);

      if (featureFlag('mockNotesToHank')) {
        const mockDelay = 500 + Math.random() * 1000;
        setTimeout(() => {
          const mockResponse = generateMockResponse(msg);
          handleAdapterMessage(mockResponse);
        }, mockDelay);
      } else {
        if (adapter) {
          adapter.sendMessage(msg);
        }
      }

      setInputText('');
      setPendingPhotos([]);
      setPendingAttachments([]);
      setSelectedAnswerIds([]);
      setPressedActionButtonIds([]);
    },
    [
      inputText,
      selectedAnswerIds,
      pressedActionButtonIds,
      getPhotoPayload,
      buildConversationContext,
      persistConversation,
      handleAdapterMessage,
    ]
  );

  const sendMessage = useCallback(() => {
    doSend();
  }, [doSend]);

  const selectAnswer = useCallback(
    (
      _questionIndex: number,
      _answerIndex: number,
      answerId: string,
      answerText?: string
    ) => {
      setSelectedAnswerIds((prev) => {
        if (prev.includes(answerId)) {
          return prev;
        }
        return [...prev, answerId];
      });
      if (answerText) {
        doSend(answerText);
      }
    },
    [doSend]
  );

  const pressActionButton = useCallback(
    (actionId: string, label?: string, clientCommand?: string) => {
      setPressedActionButtonIds((prev) => {
        if (prev.includes(actionId)) {
          return prev;
        }
        return [...prev, actionId];
      });

      if (clientCommand) {
        const localRequestMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'user',
          text: label
            ? `${label} (local request)`
            : `Action: ${clientCommand} (local request)`,
          timestamp: Date.now(),
          pressedActionButtonIds: [actionId],
        };
        setMessages((prev) => {
          const updated = [...prev, localRequestMessage];
          persistConversation(updated);
          return updated;
        });

        switch (clientCommand) {
          case 'hank':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: hank (start AI Note)`
            );
            onStartHank?.();
            break;
          case 'manual':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: manual (start Manual AI Note)`
            );
            onStartManual?.();
            break;
          case 'upload':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: upload (open attachment picker)`
            );
            onOpenAttachmentPicker?.();
            break;
          case 'orderEstimate':
            LOG.info(`${LOG_PREFIX} Executing clientCommand: orderEstimate`);
            onOrderEstimate?.();
            break;
          case 'submitClaim':
            LOG.info(`${LOG_PREFIX} Executing clientCommand: submitClaim`);
            onSubmitClaim?.();
            break;
          default:
            LOG.warn(`${LOG_PREFIX} Unknown clientCommand: ${clientCommand}`);
        }
        return;
      }

      if (label) {
        doSend(label, [actionId]);
      }
    },
    [
      doSend,
      onStartHank,
      onStartManual,
      onOpenAttachmentPicker,
      onOrderEstimate,
      onSubmitClaim,
      persistConversation,
    ]
  );

  const addAttachment = useCallback((attachment: AttachmentItem) => {
    if (attachment.type === 'photo') {
      setPendingPhotos((prev) => [...prev, attachment]);
    } else {
      setPendingAttachments((prev) => [...prev, attachment]);
    }
  }, []);

  const addAttachments = useCallback((attachments: AttachmentItem[]) => {
    const photos = attachments.filter((a) => a.type === 'photo');
    const files = attachments.filter((a) => a.type === 'file');
    if (photos.length > 0) {
      setPendingPhotos((prev) => [...prev, ...photos]);
    }
    if (files.length > 0) {
      setPendingAttachments((prev) => [...prev, ...files]);
    }
  }, []);

  const removeAttachment = useCallback(
    (index: number) => {
      const photoCount = pendingPhotos.length;
      if (index < photoCount) {
        setPendingPhotos((prev) => prev.filter((_, i) => i !== index));
      } else {
        const adjustedIndex = index - photoCount;
        setPendingAttachments((prev) =>
          prev.filter((_, i) => i !== adjustedIndex)
        );
      }
    },
    [pendingPhotos.length]
  );

  const retryLastMessage = useCallback(() => {
    const adapter = adapterRef.current;
    const lastMsg = lastUserMessageRef.current;
    if (!adapter || !lastMsg) {
      LOG.warn(`${LOG_PREFIX} Cannot retry: no adapter or last message`);
      return;
    }

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.isError) {
        return prev.slice(0, -1);
      }
      return prev;
    });

    adapter.sendMessage(lastMsg);
    setIsWaitingForResponse(true);
  }, []);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setIsWaitingForResponse(false);
    setInputText('');
    setPendingPhotos([]);
    setPendingAttachments([]);
    setSelectedAnswerIds([]);
    setPressedActionButtonIds([]);
    hasSentInitialRef.current = false;
    shouldIncludeContextRef.current = false;
    lastUserMessageRef.current = null;

    const currentDealId = dealIdRef.current;
    if (currentDealId) {
      try {
        localStorage.removeItem(getStorageKey(currentDealId));
      } catch (error) {
        LOG.error(
          `${LOG_PREFIX} Failed to clear persisted conversation`,
          error
        );
      }
    }
  }, []);

  // =========================================================================
  // Context Value
  // =========================================================================

  const contextValue = useMemo<NotesToHankContextType>(
    () => ({
      connectionState,
      messages,
      isWaitingForResponse,
      inputText,
      setInputText,
      pendingPhotos,
      pendingAttachments,
      selectedAnswerIds,
      pressedActionButtonIds,
      sendMessage,
      selectAnswer,
      pressActionButton,
      addAttachment,
      addAttachments,
      removeAttachment,
      retryLastMessage,
      clearConversation,
    }),
    [
      connectionState,
      messages,
      isWaitingForResponse,
      inputText,
      pendingPhotos,
      pendingAttachments,
      selectedAnswerIds,
      pressedActionButtonIds,
      sendMessage,
      selectAnswer,
      pressActionButton,
      addAttachment,
      addAttachments,
      removeAttachment,
      retryLastMessage,
      clearConversation,
    ]
  );

  return (
    <NotesToHankContext.Provider value={contextValue}>
      {children}
    </NotesToHankContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export const useNotesToHank = (): NotesToHankContextType => {
  const context = useContext(NotesToHankContext);
  if (!context) {
    throw new Error('useNotesToHank must be used within a NotesToHankProvider');
  }
  return context;
};
